import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useKindList } from '@/api/queries/catalog-queries'
import { Form } from '@/components/forms/form'
import { FormField } from '@/components/forms/form-field'
import { FormItem } from '@/components/forms/form-item'
import { Button } from '@/components/ui/button'
import { Field, FieldContent } from '@/components/ui/field'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { useCartaoAmareloForm } from '@/hooks/forms/use-cartao-amarelo-form'
import { useFormErrorFocus } from '@/hooks/forms/use-form-error-focus'
import type { CartaoAmareloFormData } from '@/schemas/cartao-amarelo-schema'
import type { CartaoAmarelo, Potential as PotentialValue } from '@/types'
import type { OrgCR } from '@/types/organization'
import { formatDateYMD, parseStringToDateForDisplay } from '@/utils/formatDate'
import { convertHeicIfNeeded } from '@/utils/image-compression'
import {
	INSPECTION_COLORS,
	InspectionDateField,
	InspectionPhotoField,
} from '../inspections'
import { Spinner } from '../ui/spinner'

interface PersonEdit {
	id: string
	name: string
	badge: string
}

export interface CorrectInspectionFormProps {
	initial?: {
		id: 'insp-001'
		type: 'yellow-card'
		date: '01/01/2025'
		leaderId: '0a4545f4-dbcb-465c-94fa-dca128e379e2' // Ana Segurança
		branchId: '1'
		crId: '0ff2cd66-7479-4d30-97dc-0751898a7d32'
		potential: 'n1'
		issuerId: '9d4f9f4c-9d52-4f88-8c57-0c9c8c5a9999' // Camila Oliveira
		typologyId: '1'
		riskId: '1-1'
		unsafeDescription: 'Trabalhador operando máquina sem proteção adequada'
		safeDescription: 'Instalar proteção de segurança na máquina e treinar operador'
		photoUrl: 'https://example.com/photo.jpg'
	}
	cartaoAmareloSelected?: CartaoAmarelo
	persons: PersonEdit[]
	crList?: OrgCR[]
	isLoading?: boolean
	onSave: (data: {
		crId?: string
		date?: Date
		leaderId?: string
		issuerId?: string
		typologyId?: string
		riskId?: string
		unsafeDescription?: string
		safeDescription?: string
		potential?: PotentialValue
		photo?: File
		removeImage?: boolean
	}) => void
	onClose: () => void
}

export function CorrectInspectionFormContent({
	initial,
	cartaoAmareloSelected,
	persons,
	crList,
	isLoading = false,
	onSave,
	onClose,
}: CorrectInspectionFormProps) {
	const { t } = useTranslation()
	const form = useCartaoAmareloForm()
	const {
		setFocus,
		formState: { errors },
	} = form
	const { handleError } = useFormErrorFocus(errors, setFocus)

	const kindId = form.watch('kindId')
	const image = form.watch('image')
	const [photoPreview, setPhotoPreview] = useState<string | null>(
		cartaoAmareloSelected?.imagePath
			? cartaoAmareloSelected.imagePath
			: (initial?.photoUrl ?? null),
	)
	const [removeImage, setRemoveImage] = useState(false)
	const [selectedCrId, setSelectedCrId] = useState<string>(
		cartaoAmareloSelected?.cr.id ?? '',
	)

	const { data: typologyData } = useKindList()
	const selectedKind = typologyData?.items.find((k) => k.id === kindId)

	const colors = INSPECTION_COLORS.sunnyYellow

	const personOptions = useMemo(() => {
		const mapById = new Map<string, PersonEdit>()

		for (const person of persons) {
			mapById.set(person.id, person)
		}

		if (cartaoAmareloSelected?.leader) {
			mapById.set(cartaoAmareloSelected.leader.id, {
				id: cartaoAmareloSelected.leader.id,
				name: cartaoAmareloSelected.leader.name,
				badge: cartaoAmareloSelected.leader.badge,
			})
		}

		if (cartaoAmareloSelected?.employee) {
			mapById.set(cartaoAmareloSelected.employee.id, {
				id: cartaoAmareloSelected.employee.id,
				name: cartaoAmareloSelected.employee.name,
				badge: cartaoAmareloSelected.employee.badge,
			})
		}

		return Array.from(mapById.values()).map((person) => ({
			value: person.id,
			label: `${person.name} (${person.badge})`,
		}))
	}, [cartaoAmareloSelected, persons])

	useEffect(() => {
		const sourceDate =
			cartaoAmareloSelected?.date.toString() ?? initial?.date ?? ''
		const parsedDate = parseStringToDateForDisplay(sourceDate)
		const sourcePotential = (
			cartaoAmareloSelected?.potential ??
			initial?.potential ??
			'n5'
		).toLowerCase() as PotentialValue

		form.reset({
			date: parsedDate ? formatDateYMD(parsedDate) : '',
			employeeId: cartaoAmareloSelected?.employee.id ?? initial?.issuerId ?? '',
			leaderId: cartaoAmareloSelected?.leader.id ?? initial?.leaderId ?? '',
			kindId: cartaoAmareloSelected?.kind.id ?? initial?.typologyId ?? '',
			riskId: cartaoAmareloSelected?.risk.id ?? initial?.riskId ?? '',
			unsafeDescription:
				cartaoAmareloSelected?.unsafeDescription ??
				initial?.unsafeDescription ??
				'',
			safeDescription:
				cartaoAmareloSelected?.safeDescription ??
				initial?.safeDescription ??
				'',
			potential: sourcePotential,
			image: undefined,
		})

		setPhotoPreview(
			cartaoAmareloSelected?.imagePath
				? cartaoAmareloSelected.imagePath
				: (initial?.photoUrl ?? null),
		)
		setRemoveImage(false)
		setSelectedCrId(cartaoAmareloSelected?.cr.id ?? '')
	}, [cartaoAmareloSelected, form, initial])

	// Reset risk when typology changes
	const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const picked = e.target.files?.[0]
		if (!picked) return
		const file = await convertHeicIfNeeded(picked)
		form.setValue('image', file, {
			shouldDirty: true,
			shouldValidate: true,
		})
		setRemoveImage(false)
		const url = URL.createObjectURL(file)
		setPhotoPreview(url)
	}

	const handlePhotoRemove = () => {
		if (photoPreview?.startsWith('blob:')) {
			URL.revokeObjectURL(photoPreview)
		}
		form.setValue('image', undefined, {
			shouldDirty: true,
			shouldValidate: true,
		})
		setPhotoPreview(null)
		setRemoveImage(true)
	}

	const handleSave = (formData: CartaoAmareloFormData) => {
		if (kindId && (selectedKind?.risks ?? []).length === 0) {
			toast.error('A tipologia selecionada não possui riscos disponíveis')
			return
		}

		const parsedDate = parseStringToDateForDisplay(formData.date)
		if (!parsedDate) {
			toast.error(t('common.dateEmpty'))
			return
		}

		// Envia apenas os campos que o usuario alterou (diff contra o form.reset inicial)
		const { dirtyFields } = form.formState
		const payload: Parameters<typeof onSave>[0] = {}

		if (selectedCrId && selectedCrId !== (cartaoAmareloSelected?.cr.id ?? '')) {
			payload.crId = selectedCrId
		}
		if (dirtyFields.date) {
			payload.date = parsedDate
		}
		if (dirtyFields.leaderId) {
			payload.leaderId = formData.leaderId
		}
		if (dirtyFields.employeeId) {
			payload.issuerId = formData.employeeId || undefined
		}
		if (dirtyFields.kindId) {
			payload.typologyId = formData.kindId || undefined
		}
		if (dirtyFields.riskId) {
			payload.riskId = formData.riskId || undefined
		}
		if (dirtyFields.unsafeDescription) {
			payload.unsafeDescription = formData.unsafeDescription
		}
		if (dirtyFields.safeDescription) {
			payload.safeDescription = formData.safeDescription
		}
		if (dirtyFields.potential) {
			payload.potential = formData.potential as PotentialValue
		}
		if (image) {
			payload.photo = image
		}
		if (removeImage && cartaoAmareloSelected?.imagePath) {
			payload.removeImage = true
		}

		if (Object.keys(payload).length === 0) {
			onClose()
			return
		}

		onSave(payload)
	}

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(handleSave, handleError)}
				className="flex flex-col gap-5 px-1"
			>
				{crList && crList.length > 0 && (
					<FormItem
						label={t('inspections.common.contract')}
						isRequired={false}
						error=""
					>
						<FieldContent fieldName="crId">
							<SearchableSelect
								id="yellow-card-edit-cr"
								nameField="crId"
								value={selectedCrId}
								onValueChange={setSelectedCrId}
								options={crList.map((cr) => ({ value: cr.id, label: cr.name }))}
								placeholder={t('inspections.common.contract')}
								searchPlaceholder={t('common.search')}
							/>
						</FieldContent>
					</FormItem>
				)}

				<FormField
					name="date"
					control={form.control}
					render={({ field }) => (
						<FormItem
							label={t('administration.correctInspection.dateLabel')}
							error={form.formState.errors.date?.message}
						>
							<InspectionDateField
								value={
									field.value
										? parseStringToDateForDisplay(field.value)
										: undefined
								}
								onChange={(date) =>
									field.onChange(date ? formatDateYMD(date) : '')
								}
							/>
						</FormItem>
					)}
				/>

				<Separator className="bg-border/60" />

				<FormField
					name="leaderId"
					control={form.control}
					render={({ field }) => (
						<FormItem
							label={t('administration.correctInspection.leaderFilter')}
							error={form.formState.errors.leaderId?.message}
						>
							<FieldContent fieldName={field.name}>
								<SearchableSelect
									id="inspection-leader"
									nameField={field.name}
									ref={field.ref}
									value={field.value}
									onValueChange={field.onChange}
									options={personOptions}
									placeholder={t(
										'administration.correctInspection.leaderPlaceholder',
									)}
									searchPlaceholder={t('common.search')}
								/>
							</FieldContent>
						</FormItem>
					)}
				/>

				<FormField
					name="employeeId"
					control={form.control}
					render={({ field }) => (
						<FormItem
							label={t('administration.correctInspectionForm.issuerLabel')}
							error={form.formState.errors.employeeId?.message}
						>
							<FieldContent fieldName={field.name}>
								<SearchableSelect
									id="inspection-issuer"
									nameField={field.name}
									ref={field.ref}
									value={field.value}
									onValueChange={field.onChange}
									options={personOptions}
									placeholder={t(
										'administration.correctInspectionForm.issuerPlaceholder',
									)}
									searchPlaceholder={t('common.search')}
								/>
							</FieldContent>
						</FormItem>
					)}
				/>

				<FormField
					name="kindId"
					control={form.control}
					render={({ field }) => (
						<FormItem
							label={t('administration.correctInspectionForm.typologyLabel')}
							error={form.formState.errors.kindId?.message}
						>
							<FieldContent fieldName={field.name}>
								<NativeSelect
									id="inspection-typology"
									name={field.name}
									ref={field.ref}
									value={field.value}
									onChange={(e) => {
										field.onChange(e)
										form.setValue('riskId', '', {
											shouldDirty: true,
											shouldValidate: true,
										})
									}}
								>
									<NativeSelectOption value="" disabled>
										{t(
											'administration.correctInspectionForm.typologyPlaceholder',
										)}
									</NativeSelectOption>
									{typologyData?.items.map((typology) => (
										<NativeSelectOption key={typology.id} value={typology.id}>
											{typology.name}
										</NativeSelectOption>
									))}
								</NativeSelect>
							</FieldContent>
						</FormItem>
					)}
				/>

				<FormField
					name="riskId"
					control={form.control}
					render={({ field }) => (
						<FormItem
							label={t('administration.correctInspectionForm.riskLabel')}
							error={form.formState.errors.riskId?.message}
						>
							<Field className="animate-in fade-in slide-in-from-top-2 duration-200">
								<FieldContent fieldName={field.name}>
									<NativeSelect
										id="inspection-risk"
										name={field.name}
										ref={field.ref}
										value={field.value}
										onChange={field.onChange}
										disabled={!kindId || !selectedKind}
									>
										<NativeSelectOption value="" disabled>
											{t(
												'administration.correctInspectionForm.riskPlaceholder',
											)}
										</NativeSelectOption>
										{selectedKind?.risks.map((risk) => (
											<NativeSelectOption key={risk.id} value={risk.id}>
												{risk.name}
											</NativeSelectOption>
										))}
									</NativeSelect>
								</FieldContent>
							</Field>
						</FormItem>
					)}
				/>

				<Separator className="bg-border/60" />

				<FormField
					name="unsafeDescription"
					control={form.control}
					render={({ field }) => (
						<FormItem
							label={t(
								'administration.correctInspectionForm.unsafeDescriptionLabel',
							)}
							error={form.formState.errors.unsafeDescription?.message}
						>
							<FieldContent fieldName={field.name}>
								<Textarea
									name={field.name}
									ref={field.ref}
									value={field.value}
									onChange={field.onChange}
									placeholder={t(
										'administration.correctInspectionForm.unsafeDescriptionPlaceholder',
									)}
									rows={3}
								/>
							</FieldContent>
						</FormItem>
					)}
				/>

				<FormField
					name="safeDescription"
					control={form.control}
					render={({ field }) => (
						<FormItem
							label={t(
								'administration.correctInspectionForm.safeDescriptionLabel',
							)}
							error={form.formState.errors.safeDescription?.message}
						>
							<FieldContent fieldName={field.name}>
								<Textarea
									name={field.name}
									ref={field.ref}
									value={field.value}
									onChange={field.onChange}
									placeholder={t(
										'administration.correctInspectionForm.safeDescriptionPlaceholder',
									)}
									rows={3}
								/>
							</FieldContent>
						</FormItem>
					)}
				/>

				<FormField
					name="image"
					control={form.control}
					render={() => (
						<FormItem
							label={t('administration.correctInspectionForm.photoLabel')}
							isRequired={false}
							error=""
						>
							<InspectionPhotoField
								attachLabel={t(
									'administration.correctInspectionForm.photoAttach',
								)}
								altText={t('administration.correctInspectionForm.photoAlt')}
								preview={photoPreview}
								onFileChange={handlePhotoChange}
								onRemove={handlePhotoRemove}
								colors={colors}
							/>
						</FormItem>
					)}
				/>

				<div className="flex gap-3 pt-1">
					{isLoading ? (
						<Button
							disabled={isLoading}
							type="button"
							className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
						>
							<Spinner />
							{t('administration.correctInspection.saving')}
						</Button>
					) : (
						<>
							<Button
								type="button"
								variant="cancel"
								onClick={onClose}
								className="flex-1"
							>
								{t('administration.correctInspection.cancelButton')}
							</Button>
							<Button
								disabled={isLoading}
								type="submit"
								className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
							>
								{t('administration.correctInspection.saveEdit')}
							</Button>
						</>
					)}
				</div>
			</form>
		</Form>
	)
}
